import React, {
  memo,
  useMemo,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';
import cn from 'classnames';
import { useRouter } from 'next/router';
import useSWRInfinite from 'swr/infinite';
import queryString from 'query-string';
import { useRecoilValue, useRecoilState } from 'recoil';
import { Trans, useTranslation } from 'next-i18next';
import { Icon } from 'components';
import WalkthroughTooltip, {
  WalkthroughTooltipType,
  DashboardWalkthroughProgressCount,
} from 'components/WalkthroughTooltip';

import { ActionOption } from 'components/ActionMenu';
import { Row } from 'components/layout';
import { ButtonV3 } from 'components/ComponentV2';
import TouchpointCard from 'components/candidate/TouchpointCard';
import { Button } from 'components/form';
import { registrationDetailsModalInstance } from 'components/candidate/RegistrationDetailsModal';
import {
  TouchpointNotifications,
  NotificationType,
} from 'components/candidate/Notifications/TouchpointNotifications.component';
import { TodoSkeleton } from 'components/skeletons/dashboard.component';
import Tooltip from 'components/Tooltip';
import { applyConfirmationModalInstance } from 'components/candidate/ApplyConfirmationModal';
import { externalApplyModalInstance } from 'components/candidate/ExternalApplyModal';
import { useCommon } from 'hooks/useCommon';
import { useNotification } from 'hooks/useNotification';
import { FLAG_STATUS_CODES, useCandidate } from 'hooks/useCandidate';
import { useResponsive } from 'hooks/useResponsive';
import ResponseModal from 'pages/candidate/applications/ResponseModal/ResponseModal.component';
import { walkthroughTooltipState } from 'lib/atoms/walkthroughTooltipAtom';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { PAGE_ROUTES } from 'lib/page-routes';
import { ApplicationResponseModalEnum } from 'lib/models/candidate-applications';
import { useApp } from 'lib/contexts/save-in-app-context';
import { useAuth } from 'lib/providers/AuthProvider';
import { get } from 'lib/utils/http';
import { API_ROUTES } from 'lib/api-routes';
import {
  parsePaginatedResponse,
  parseResponse,
  parseArrayResponse,
} from 'lib/utils/parser';
import {
  OpportunityAttributes,
  OpportunitiesResponse,
  TouchpointState,
} from 'lib/models/opportunity';
import { TouchpointStatus, TouchpointType } from 'lib/models/touchpoint';
import { CompanyAttributes } from 'lib/models/company';
import { StudentSocietyAttributes } from 'lib/models/student-society';
import { JobTouchpointable } from 'lib/models/job';
import { FollowingAttributes } from 'lib/models/following';
import { EventTouchpointable } from 'lib/models/event';
import { useCandidateDashboard } from 'lib/providers/CandidateDashboardProvider';
import { getOpportunityPublicPagePathname } from 'utils/urls';
import { getEventButtonText } from 'utils/format';
import { AutofillTooltipContent } from '../AutofillTooltip/AutofillTooltip.component';

import styles from '../Section.module.scss';

const PER_PAGE_COUNT = 3;

type StatusObject = {
  [key: string]: string;
};
export interface TodoSectionProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const TodoSection: React.FC<TodoSectionProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: TodoSectionProps) => {
    const router = useRouter();
    const { updateCount } = useCandidate();
    const notificationInstance = useNotification();
    const screen = useResponsive();
    const { headers } = useRecoilValue(httpHeadersState);
    const {
      writeApplicationModalType,
      setWriteApplicationModalType,
      handleAutofillApplicationsClick,
    } = useApp();
    const { candidate, isCandidate } = useAuth();
    const {
      onShare,
      sendInvalidURLReport,
      applyOpportunity,
      removeOpportunity,
      onApplyClick,
    } = useCommon();
    const [activeStatusTouchpoint, setActiveStatusTouchpoints] =
      useState<StatusObject>({});

    const touchpointNotificationInstance = TouchpointNotifications();
    const { candidateDashboard, refetchCandidateDashboard } =
      useCandidateDashboard();
    const [t] = useTranslation('candidate-dashboard');

    const {
      profile_complete,
      show_chrome_externsion_banner,
      show_complete_profile_banner,
      external_registrated_event_count,
      external_applied_non_event_count,
    } = candidate || {};

    const [walkthroughTooltip, _] = useRecoilState(walkthroughTooltipState);
    const generateCVRef = useRef<HTMLParagraphElement>(null);

    const {
      started_application_count = [],
      saved_application_count = [],
      extension_installed,
    } = useMemo(() => {
      const {
        extension_installed,
        started_application_count,
        saved_application_count,
      } = candidateDashboard || {};

      return {
        extension_installed,
        started_application_count,
        saved_application_count,
      };
    }, [candidateDashboard]);

    const endOfPageRef = React.useRef<HTMLDivElement | null>(null);
    const scrollToBottom = () => {
      endOfPageRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
    };

    const getTouchpointListKey = (pageIndex: number) => {
      const params = {
        page: pageIndex + 1,
        per_page: PER_PAGE_COUNT,
        with_details: true,
        remove_expired: true,
      };
      const qs = queryString.stringify(params, {
        arrayFormat: 'bracket',
        skipEmptyString: true,
      });
      const pathname = `${API_ROUTES.CANDIDATE_TOUCHPOINT}${API_ROUTES.TODO_TOUCHPOINTS}?${qs}`;

      return [pathname, headers];
    };

    const {
      data: touchpointsResponse,
      error: touchpointsError,
      size,
      setSize,
      mutate,
    } = useSWRInfinite<OpportunitiesResponse>(getTouchpointListKey, get, {
      revalidateFirstPage: true,
    });

    const onMutateApi = () => mutate();
    const isAPILoading = !touchpointsResponse && !touchpointsError;
    const isLoadingMore =
      isAPILoading ||
      (size > 0 &&
        touchpointsResponse &&
        typeof touchpointsResponse[size - 1] === 'undefined');

    const touchpoints = useMemo(() => {
      return parsePaginatedResponse<OpportunityAttributes>(touchpointsResponse);
    }, [touchpointsResponse]);

    const totalCount = useMemo(
      () =>
        touchpointsResponse ? touchpointsResponse[0]?.meta?.total : undefined,
      [touchpointsResponse]
    );

    const expiringCount = touchpointsResponse
      ? touchpointsResponse[0]?.meta?.expiring_soon_count
      : undefined;

    const hasMoreRecords = useMemo(() => {
      const firstApplicationList = Array.isArray(touchpointsResponse)
        ? touchpointsResponse[0]
        : null;
      if (firstApplicationList) {
        return (firstApplicationList?.meta?.total || 0) > size * PER_PAGE_COUNT;
      } else {
        return false;
      }
    }, [touchpointsResponse, size]);

    useEffect(() => {
      if (!isLoadingMore && size > 1) {
        scrollToBottom();
      }
    }, [isLoadingMore, size]);

    useEffect(() => {
      setSize(1);

      return () => {
        endOfPageRef.current = null;
        setSize(1);
      };
    }, []);

    const writeApplicationsModal = useMemo(() => {
      if (
        writeApplicationModalType &&
        writeApplicationModalType.type !==
          ApplicationResponseModalEnum.coverLetter
      ) {
        return (
          <ResponseModal
            modalType={writeApplicationModalType?.type}
            isOpen={true}
            application={writeApplicationModalType?.application || undefined}
            onClose={() => setWriteApplicationModalType(null)}
          />
        );
      }
    }, [writeApplicationModalType]);

    const onRemoveOpportunity = async (id: string, status: string) => {
      try {
        const response = await removeOpportunity(id, headers, status);
        if (response) {
          await onMutateApi();
          notificationInstance.success({
            title: 'Success!',
            message: `Opportunity ${status} successfully.`,
          });
        }
      } catch (error) {
        notificationInstance.handleExceptionError(error);
      }
    };

    const showTouchpointNotification = useCallback(
      (apply_url: string, type: string, willJoinWaitlist: boolean) => {
        if (apply_url) {
          touchpointNotificationInstance({
            notificationType: NotificationType.EXTERNAL_APPLY,
            type: type,
            eventCount: external_registrated_event_count,
            nonEventCount: external_applied_non_event_count,
          });
          if (type === TouchpointType.Event) {
            updateCount({
              external_registrated_event_count:
                external_registrated_event_count! + 1,
            });
          } else {
            updateCount({
              external_applied_non_event_count:
                external_applied_non_event_count! + 1,
            });
          }
        } else {
          if (type === TouchpointType.Event) {
            touchpointNotificationInstance({
              notificationType: willJoinWaitlist
                ? NotificationType.INTERNAL_EVENT_WAITLIST
                : NotificationType.INTERNAL_EVENT_REGISTERED,
            });
          } else {
            touchpointNotificationInstance({
              notificationType: NotificationType.INTERNAL_JOB_APPLY,
              type: type,
            });
          }
        }
      },
      []
    );

    const onApplyOpportunityMark = async (
      id: string,
      apply_url: string,
      type: string,
      willJoinWaitlist: boolean
    ) => {
      if (id) {
        try {
          const response = await applyOpportunity(id);
          if (response) {
            setActiveStatusTouchpoints({
              ...activeStatusTouchpoint,
              [id]: TouchpointStatus.APPLIED,
            });
            showTouchpointNotification(apply_url, type, willJoinWaitlist);
            onMutateApi();
            refetchCandidateDashboard();
          }
        } catch (error) {
          console.error(error);
        }
      } else {
        return;
      }
    };

    const redirectToExtLink = async (
      id: string,
      apply_url: string,
      type: string,
      willJoinWaitlist: boolean
    ) => {
      window.open(apply_url, '_blank');
      try {
        const flag = await applyConfirmationModalInstance({
          isEvent: type === TouchpointType.Event,
        });
        if (flag === FLAG_STATUS_CODES.CONFIRM_APPLY_YES) {
          onApplyOpportunityMark(id, apply_url, type, willJoinWaitlist);
        } else if (flag === FLAG_STATUS_CODES.CONFIRM_APPLY_INCORRECT_LINK) {
          sendInvalidURLReport({ id });
        }
      } catch (error) {
        console.error(error);
      }
    };

    const onApplyOpportunity = async (
      id: string,
      apply_url: string,
      type: string,
      willJoinWaitlist: boolean
    ) => {
      if (id) {
        await onApplyClick({ touchpointId: id });
        setActiveStatusTouchpoints({
          ...activeStatusTouchpoint,
          [id]: TouchpointStatus.STARTED,
        });
        if (apply_url) {
          if (
            (isCandidate &&
              !show_chrome_externsion_banner &&
              !show_complete_profile_banner) ||
            (!show_complete_profile_banner && extension_installed) ||
            (!show_chrome_externsion_banner && profile_complete) ||
            (extension_installed && profile_complete)
          ) {
            redirectToExtLink(id, apply_url, type, willJoinWaitlist);
          } else {
            try {
              await externalApplyModalInstance({ apply_url });
              redirectToExtLink(id, apply_url, type, willJoinWaitlist);
            } catch (err) {
              console.error(err);
            }
          }
        } else {
          try {
            const flag = await registrationDetailsModalInstance({
              touchpointId: id,
            });
            if (
              flag === FLAG_STATUS_CODES.NEW_USER_REGISTER_SUCCESS ||
              flag === FLAG_STATUS_CODES.EXISTING_USER_REGISTER_SUCCESS
            ) {
              onApplyOpportunityMark(id, apply_url, type, willJoinWaitlist);
            } else {
              return;
            }
          } catch (error) {
            console.error(error);
          }
        }
      }
    };

    const getSavedActionMenu = (
      id: string,
      apply_url: string,
      title: string,
      publicUrl: string,
      applied: boolean,
      type: string,
      started: boolean | null,
      touchpointableResponse: EventTouchpointable,
      state: TouchpointState,
      applications: number,
      is_eligible_for_auto_apply: boolean
    ) => {
      const touchpoint = parseResponse(
        touchpointableResponse as EventTouchpointable
      );
      const {
        enforce_capacity,
        waitlist,
        enforce_waitlist_for_all,
        capacity,
        is_capacity_reached,
      } = touchpoint;
      const willJoinWaitlist =
        enforce_waitlist_for_all || (is_capacity_reached && waitlist);

      return [
        {
          id: '1',
          label:
            type === TouchpointType.Event
              ? getEventButtonText(
                  state,
                  enforce_capacity || false,
                  capacity || 0,
                  (touchpoint && capacity - applications) || undefined,
                  waitlist || false,
                  enforce_waitlist_for_all || false
                )
              : applied
              ? 'Applied'
              : is_eligible_for_auto_apply
              ? 'Quick Apply'
              : 'Apply',
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            onApplyOpportunity(id, apply_url, type, willJoinWaitlist);
            event.stopPropagation();
          },
        },
        ...(started
          ? [
              {
                id: '2',
                label:
                  type === TouchpointType.Event
                    ? 'Mark as registered'
                    : 'Mark as applied',
                onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault();
                  onApplyOpportunityMark(id, apply_url, type, willJoinWaitlist);
                  event.stopPropagation();
                },
              },
            ]
          : []),
        {
          id: '3',
          label: 'Share',
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            onShare(title, publicUrl);
          },
        },
        {
          id: '4',
          label: 'Report',
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            sendInvalidURLReport({ id });
          },
        },
        {
          id: '5',
          label: 'Remove',
          onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            onRemoveOpportunity(id, 'removed');
          },
          danger: true,
        },
      ];
    };

    const isCandidateDashboard =
      router.pathname === PAGE_ROUTES.CANDIDATE_DASHBOARD;

    const getTouchpointCard = (touchpoints: Array<OpportunityAttributes>) => {
      return (
        <div className={styles.touchpointcardList}>
          {touchpoints?.map(
            ({
              id,
              title,
              start_date,
              creatable_for,
              touchpointable_type,
              slug,
              application_deadline,
              apply_url,
              pay_currency,
              touchpointable: touchpointableResponse,
              kind,
              cities,
              is_applied,
              started,
              cohost_applications: cohostApplicationsResponse,
              following: followingResponse,
              state,
              applications,
              is_eligible_for_auto_apply,
            }) => {
              const createdBy = parseResponse<
                CompanyAttributes | StudentSocietyAttributes
              >(creatable_for);
              const { name, icon_url, is_verified, huzzle_verified } =
                createdBy;
              let maximumSalary, minimumSalary;
              if (
                touchpointable_type === TouchpointType.Job ||
                touchpointable_type === TouchpointType.Internship
              ) {
                const touchpointable = parseResponse(
                  touchpointableResponse as JobTouchpointable
                );
                maximumSalary = touchpointable?.maximum_salary;
                minimumSalary = touchpointable?.minimum_salary;
              }

              const publicPageUrl = `${
                window.location.origin
              }${getOpportunityPublicPagePathname(touchpointable_type, slug)}`;

              const cohost_applications = parseArrayResponse(
                cohostApplicationsResponse
              );
              const following =
                parseResponse<FollowingAttributes>(followingResponse);

              const getMenuOptions = (id: string) => {
                return getSavedActionMenu(
                  id,
                  apply_url,
                  title,
                  publicPageUrl,
                  is_applied,
                  touchpointable_type as string,
                  started,
                  touchpointableResponse as EventTouchpointable,
                  state,
                  applications,
                  is_eligible_for_auto_apply
                );
              };

              return (
                <TouchpointCard
                  state={state}
                  applications={applications}
                  key={id}
                  icon_url={icon_url}
                  id={id}
                  touchpointable={
                    TouchpointType.Event
                      ? (touchpointableResponse as
                          | EventTouchpointable
                          | undefined)
                      : null
                  }
                  title={title}
                  slug={slug}
                  createdBy={name}
                  startDate={start_date}
                  created_at={''}
                  application_deadline={application_deadline}
                  is_verified={is_verified}
                  touchpointable_type={touchpointable_type as TouchpointType}
                  maximum_salary={maximumSalary}
                  minimum_salary={minimumSalary}
                  pay_currency={pay_currency || 'GBP'}
                  isExpired={false}
                  url={publicPageUrl}
                  className={styles.touchpointcard}
                  isEasyApply={
                    touchpointable_type !== TouchpointType.Event && !apply_url
                  }
                  isNewSet={false}
                  huzzle_verified={huzzle_verified}
                  kind={kind}
                  isOnboarding
                  cities={cities}
                  showManagerProgress
                  cohost_applications={cohost_applications}
                  following={following}
                  apply_url={apply_url}
                  mutateApi={onMutateApi}
                  extension_installed={extension_installed || true}
                  menuOptions={getMenuOptions(id) as Array<ActionOption>}
                  activeStatus={
                    activeStatusTouchpoint[id]
                      ? activeStatusTouchpoint[id]
                      : null
                  }
                  isStarted={started}
                  color={isCandidateDashboard ? 'white' : 'secondary'}
                  is_eligible_for_auto_apply={is_eligible_for_auto_apply}
                />
              );
            }
          )}
        </div>
      );
    };

    const contentClassNames = cn(
      {
        [styles.container]: true,
      },
      { [styles.section]: true },
      { [styles['hide']]: !isCandidateDashboard && totalCount === 0 },
      { [styles['section-margin']]: !isCandidateDashboard && totalCount },
      className
    );

    return (
      <>
        <div
          className={contentClassNames}
          style={style}
          data-testid="TodoSection"
          ref={endOfPageRef}
        >
          <Row align="center" justify="space-between">
            <p className={styles.title}>
              To do{' '}
              {expiringCount ? (
                <span className={styles.new}>{expiringCount} expiring</span>
              ) : null}
            </p>
            {totalCount ? (
              <ButtonV3
                color="blue"
                variant="text"
                endIcon="right-arrow"
                onClick={() =>
                  router.push(`${PAGE_ROUTES.MANAGER}?status=Saved`)
                }
                className={styles.textbutton}
              >
                View all
              </ButtonV3>
            ) : null}
          </Row>
          {isCandidateDashboard ? (
            <div className={styles.actionRow}>
              <WalkthroughTooltip
                title={t('todo-section.walkthrough.generate-cv.title')}
                description={t('todo-section.walkthrough.generate-cv.desc')}
                buttonOneText={t('todo-section.walkthrough.generate-cv.button')}
                previousProgressWalkthrough={
                  WalkthroughTooltipType.DASHBOARD_CHANGE_PREFERENCES
                }
                walkthroughType={WalkthroughTooltipType.DASHBOARD_GENERATE_CV}
                scrollWalkthroughRef={generateCVRef}
                position={'top'}
                alignment={screen.sm ? 'start' : 'center'}
                childrenClassName={styles.walkthrough}
                progressCount={
                  DashboardWalkthroughProgressCount.DASHBOARD_TOTAL_STEPS
                }
                progressPosition={
                  DashboardWalkthroughProgressCount.DASHBOARD_GENERATE_CV
                }
              >
                <Row
                  align="center"
                  justify="center"
                  columnGap={8}
                  wrap={false}
                  onClick={() => router.push(PAGE_ROUTES.CANDIDATE_PROFILE)}
                  className={styles.actionButton}
                >
                  <Icon
                    iconName="icon_camera-flash"
                    className={styles.actionButtonIcon}
                  />
                  <p ref={generateCVRef} className={styles.actionButtonText}>
                    {t('todo-section.generate-cv')}
                  </p>
                </Row>
              </WalkthroughTooltip>
              <WalkthroughTooltip
                title={t('todo-section.walkthrough.cover-letter.title')}
                description={t('todo-section.walkthrough.cover-letter.desc')}
                buttonOneText={t(
                  'todo-section.walkthrough.cover-letter.button'
                )}
                previousProgressWalkthrough={
                  WalkthroughTooltipType.DASHBOARD_GENERATE_CV
                }
                walkthroughType={WalkthroughTooltipType.DASHBOARD_COVER_LETTER}
                position={'top'}
                alignment={screen.sm ? 'end' : 'center'}
                childrenClassName={styles.walkthrough}
                progressCount={
                  DashboardWalkthroughProgressCount.DASHBOARD_TOTAL_STEPS
                }
                progressPosition={
                  DashboardWalkthroughProgressCount.DASHBOARD_COVER_LETTER
                }
              >
                <Row
                  align="center"
                  justify="center"
                  columnGap={8}
                  wrap={false}
                  onClick={() =>
                    router.push(PAGE_ROUTES.CANDIDATE_BUILD_COVER_LETTER)
                  }
                  className={styles.actionButton}
                >
                  <Icon
                    iconName="icon_document-2"
                    className={styles.actionButtonIcon}
                  />
                  <p className={styles.actionButtonText}>
                    {t('todo-section.ai-cover-letter-builder')}
                  </p>
                </Row>
              </WalkthroughTooltip>
              <WalkthroughTooltip
                title={t('todo-section.walkthrough.ai-question-replies.title')}
                description={t(
                  'todo-section.walkthrough.ai-question-replies.desc'
                )}
                buttonOneText={t(
                  'todo-section.walkthrough.ai-question-replies.button'
                )}
                previousProgressWalkthrough={
                  WalkthroughTooltipType.DASHBOARD_COVER_LETTER
                }
                walkthroughType={
                  WalkthroughTooltipType.DASHBOARD_AI_QUESTION_REPLY
                }
                position={'top'}
                alignment={screen.sm ? 'start' : 'center'}
                childrenClassName={styles.walkthrough}
                progressCount={
                  DashboardWalkthroughProgressCount.DASHBOARD_TOTAL_STEPS
                }
                progressPosition={
                  DashboardWalkthroughProgressCount.DASHBOARD_AI_QUESTION_REPLY
                }
              >
                <Row
                  align="center"
                  justify="center"
                  columnGap={8}
                  wrap={false}
                  onClick={() => {
                    setWriteApplicationModalType({
                      type: ApplicationResponseModalEnum.questionResponse,
                      application: null,
                    });
                    close();
                  }}
                  className={styles.actionButton}
                >
                  <Icon
                    iconName="icon_magic-wand"
                    className={styles.actionButtonIcon}
                  />
                  <p className={styles.actionButtonText}>
                    {t('todo-section.ai-question-replies')}
                  </p>
                </Row>
              </WalkthroughTooltip>
              <WalkthroughTooltip
                title={
                  <Trans
                    t={t}
                    i18nKey="todo-section.walkthrough.autofill-extension.title"
                  >
                    Apply 10X faster <p> </p> with our Extension
                  </Trans>
                }
                description={t(
                  'todo-section.walkthrough.autofill-extension.desc'
                )}
                buttonOneText={t(
                  'todo-section.walkthrough.autofill-extension.button'
                )}
                previousProgressWalkthrough={
                  WalkthroughTooltipType.DASHBOARD_AI_QUESTION_REPLY
                }
                walkthroughType={
                  WalkthroughTooltipType.DASHBOARD_AUTOFILL_EXTENSION
                }
                position={'top'}
                alignment={screen.sm ? 'end' : 'center'}
                childrenClassName={styles.walkthrough}
                progressCount={
                  DashboardWalkthroughProgressCount.DASHBOARD_TOTAL_STEPS
                }
                progressPosition={
                  DashboardWalkthroughProgressCount.DASHBOARD_AUTOFILL_EXTENSION
                }
              >
                <Tooltip
                  content={<AutofillTooltipContent />}
                  containerClassName={styles.popover}
                  className={styles.menu}
                  position="top"
                  yAlign="bottom"
                  disabled={
                    screen.sm ||
                    (walkthroughTooltip[
                      WalkthroughTooltipType.DASHBOARD_AUTOFILL_EXTENSION
                    ] &&
                      !walkthroughTooltip[
                        WalkthroughTooltipType.DASHBOARD_AI_QUESTION_REPLY
                      ])
                  }
                >
                  <Row
                    align="center"
                    justify="center"
                    columnGap={8}
                    wrap={false}
                    onClick={handleAutofillApplicationsClick}
                    className={styles.actionButton}
                  >
                    <Icon
                      iconName="icon_check-all"
                      className={styles.actionButtonIcon}
                    />
                    <p className={styles.actionButtonText}>
                      {t('todo-section.autofill-applications')}
                    </p>
                  </Row>
                </Tooltip>
              </WalkthroughTooltip>
            </div>
          ) : null}
          {totalCount ? getTouchpointCard(touchpoints) : null}
          {totalCount === undefined || isLoadingMore ? <TodoSkeleton /> : null}
          {totalCount ? (
            <Row align="center" columnGap={8} className={styles.countsRow}>
              {touchpoints?.length ? (
                <p>
                  Showing {touchpoints?.length} of {totalCount}
                </p>
              ) : null}
              {hasMoreRecords ? (
                <Button
                  variant="text"
                  color="blue"
                  onClick={() => {
                    setSize(size + 1);
                  }}
                  className={styles.countsButton}
                >
                  Show more
                </Button>
              ) : null}
            </Row>
          ) : null}
          {writeApplicationsModal}
        </div>
      </>
    );
  }
);

TodoSection.displayName = 'TodoSection';
